import * as THREE from 'three/webgpu';
import {CSS2DObject} from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import {convertMToMm} from '../../../utils/unit';
import type {ReferencePlane} from '../../../reference/ReferencePlane';
import {resolveSketchDimensionQuantityKind} from '../model/SketchDimension';
import type {SketchDimensionSnapshot} from '../SketchDimensionTypes';
import {
    expandSketchDimensionArrow,
    resolveSketchDimensionLayout,
} from '../render/SketchDimensionLayoutResolver';
import type {
    SketchDimensionLayoutSegment,
    SketchDimensionReferenceAnchor,
} from '../render/SketchDimensionLayoutResolver';
import type {SmartDimensionCandidate, SmartDimensionVec2} from './SketchSmartDimensionTypes';
import {SketchDimensionGeometryBuffer} from '../render/geometry';

/** 预览线颜色（半透明蓝）。 */
const PREVIEW_LINE_COLOR = 0x1e6fff;

/** 角度弧采样段数（与持久标注渲染一致）。 */
const ARC_SAMPLE_SEGMENTS = 24;

/**
 * 按候选类型重建布局锚点（预览用完整尺寸样式，对齐 SOLIDWORKS 放置预览）。
 *
 * 说明：候选 previewAnchors 是各策略的语义点列，按核心类型映射为
 * 布局解析器的结构化锚点；映射未覆盖的类型（中心线双倍/弧长等
 * 特殊语义）返回 null，退回锚点引线预览。
 *
 * @param candidate 当前候选。
 * @returns 布局锚点；不支持完整布局预览的类型返回 null。
 */
function buildPreviewLayoutAnchors(
    candidate: SmartDimensionCandidate,
): readonly SketchDimensionReferenceAnchor[] | null {
    const anchors = candidate.previewAnchors;

    switch (candidate.dimensionKind) {
        case 'point-distance':
            return anchors.length >= 2
                ? [
                    {kind: 'point', position: anchors[0]!},
                    {kind: 'point', position: anchors[1]!},
                ]
                : null;
        case 'length':
            return anchors.length >= 2
                ? [{kind: 'line', start: anchors[0]!, end: anchors[1]!}]
                : null;
        case 'point-line-distance':
            // 策略锚点约定：[点, 线起点, 线终点]。
            return anchors.length >= 3
                ? [
                    {kind: 'point', position: anchors[0]!},
                    {kind: 'line', start: anchors[1]!, end: anchors[2]!},
                ]
                : null;
        case 'angle':
        case 'symmetric-angle':
            // 普通两线角与对称角共用锚点约定：[线1起, 线1终, 线2起, 线2终]。
            return anchors.length >= 4
                ? [
                    {kind: 'line', start: anchors[0]!, end: anchors[1]!},
                    {kind: 'line', start: anchors[2]!, end: anchors[3]!},
                ]
                : null;
        case 'three-point-angle':
            return anchors.length >= 3
                ? [
                    {kind: 'point', position: anchors[0]!},
                    {kind: 'point', position: anchors[1]!},
                    {kind: 'point', position: anchors[2]!},
                ]
                : null;
        case 'radius':
        case 'diameter': {
            const geometricRadius = candidate.currentValue / (candidate.valueScale || 1);

            return anchors.length >= 1 && geometricRadius > 0
                ? [{kind: 'circle', center: anchors[0]!, radius: geometricRadius}]
                : null;
        }
        case 'path-length': {
            /**
             * 路径长度锚点约定：previewAnchors 是各段 [起, 终] 依次平铺——
             * 成对还原为线锚点，交给布局器的引线式分支（一根引线 + 总长值，
             * 与确认落库后的标注同款；曾经走"锚点引线"退路画出多根线）。
             */
            const lines: SketchDimensionReferenceAnchor[] = [];

            for (let index = 0; index + 1 < anchors.length; index += 2) {
                lines.push({kind: 'line', start: anchors[index]!, end: anchors[index + 1]!});
            }

            return lines.length > 0 ? lines : null;
        }
        default:
            return null;
    }
}

/**
 * 智能尺寸半透明预览渲染器。
 *
 * 设计说明：
 * 1. 预览优先按完整尺寸样式渲染（复用 `resolveSketchDimensionLayout`：
 *    尺寸线/尺寸界线/箭头/角度弧随指针实时布局，对齐 SOLIDWORKS 放置预览）；
 *    布局映射未覆盖的特殊类型退回"锚点引线 + 值标签"；
 * 2. 渲染对象全部 runtime-derived，材质/几何/DOM 复用，dispose 全释放；
 * 3. 标签 `pointerEvents=none`，不参与拾取。
 */
export class SketchSmartDimensionPreviewRenderer {
    /** 预览对象容器。 */
    private readonly group = new THREE.Group();

    /** 引线几何和 position 属性的独占生命周期所有者。 */
    private readonly lineBuffer = new SketchDimensionGeometryBuffer();

    /** 引线材质（半透明虚线语义用透明实线近似）。 */
    private readonly lineMaterial = new THREE.LineBasicMaterial({
        color: PREVIEW_LINE_COLOR,
        transparent: true,
        opacity: 0.55,
        depthTest: false,
    });

    /** 引线对象。 */
    private readonly line = new THREE.LineSegments(this.lineBuffer.geometry, this.lineMaterial);

    /** 局部到世界转换复用点，避免每个顶点创建 Vector3。 */
    private readonly worldPoint = new THREE.Vector3();

    /** 值标签 DOM。 */
    private readonly labelElement: HTMLDivElement;

    /** 值标签对象。 */
    private readonly label: CSS2DObject;

    /** 宿主场景。 */
    private hostScene: THREE.Scene | null = null;

    public constructor() {
        this.group.name = 'sketch-smart-dimension-preview';
        this.group.renderOrder = 999;
        this.line.frustumCulled = false;
        this.group.add(this.line);

        this.labelElement = document.createElement('div');
        this.labelElement.style.pointerEvents = 'none';
        this.labelElement.style.padding = '1px 6px';
        this.labelElement.style.borderRadius = '3px';
        this.labelElement.style.fontSize = '12px';
        this.labelElement.style.lineHeight = '18px';
        this.labelElement.style.color = '#fff';
        this.labelElement.style.background = 'rgba(30, 111, 255, 0.75)';
        this.labelElement.style.whiteSpace = 'nowrap';
        this.label = new CSS2DObject(this.labelElement);
        this.group.add(this.label);
        this.group.visible = false;
    }

    /**
     * 挂载到场景。
     *
     * @param scene 宿主场景。
     */
    public attach(scene: THREE.Scene): void {
        if (this.hostScene === scene) {
            return;
        }
        this.detach();
        this.hostScene = scene;
        scene.add(this.group);
    }

    /** 从场景卸载。 */
    public detach(): void {
        this.hostScene?.remove(this.group);
        this.hostScene = null;
        this.group.visible = false;
    }

    /**
     * 更新预览。
     *
     * @param candidate 当前候选。
     * @param anchorWorldResolver 局部坐标 → 世界坐标换算（活动草图参考面）。
     * @param pointerLocal 指针局部坐标。
     */
    /**
     * 尺寸链基准 0 界线（SW：正式标注观感的**灰色**界线 + 端头圆圈 + "0"，
     * 不是蓝色半透明预览样式；单击放置前跟随鼠标沿 H/V 吸附）。
     *
     * @param datum 基准点局部坐标。
     * @param guideEnd 界线端点（已按 H/V 吸附）。
     * @param anchorWorldResolver 局部 → 世界换算。
     */
    public updateOrdinateDatumGuide(
        datum: SmartDimensionVec2,
        guideEnd: SmartDimensionVec2,
        anchorWorldResolver: ReferencePlane,
    ): void {
        this.applyStyle('ordinate-datum');

        const segments: SketchDimensionLayoutSegment[] = [{start: datum, end: guideEnd}];
        /** 端头小圆圈（SW 基准记号，12 段近似）。 */
        const markRadius = 0.0012;

        for (let index = 0; index < 12; index += 1) {
            const fromAngle = (index / 12) * Math.PI * 2;
            const toAngle = ((index + 1) / 12) * Math.PI * 2;

            segments.push({
                start: [
                    guideEnd[0] + Math.cos(fromAngle) * markRadius,
                    guideEnd[1] + Math.sin(fromAngle) * markRadius,
                ],
                end: [
                    guideEnd[0] + Math.cos(toAngle) * markRadius,
                    guideEnd[1] + Math.sin(toAngle) * markRadius,
                ],
            });
        }

        const position = this.lineBuffer.beginUpdate(segments.length * 2);

        segments.forEach((segment, index) => {
            anchorWorldResolver.localToWorldTo(segment.start[0], segment.start[1], this.worldPoint);
            position.setXYZ(index * 2, this.worldPoint.x, this.worldPoint.y, this.worldPoint.z);
            anchorWorldResolver.localToWorldTo(segment.end[0], segment.end[1], this.worldPoint);
            position.setXYZ(index * 2 + 1, this.worldPoint.x, this.worldPoint.y, this.worldPoint.z);
        });
        this.line.geometry = this.lineBuffer.endUpdate();

        this.labelElement.textContent = '0';
        anchorWorldResolver.localToWorldTo(guideEnd[0], guideEnd[1] + markRadius * 2.2, this.label.position);
        this.group.visible = true;
    }

    /**
     * 切换视觉样式（蓝色半透明预览 / 尺寸链基准的正式灰色）。
     *
     * @param mode 样式模式。
     */
    private applyStyle(mode: 'preview' | 'ordinate-datum'): void {
        if (mode === 'ordinate-datum') {
            this.lineMaterial.color.set(0x6b6b6b);
            this.lineMaterial.opacity = 1;
            this.labelElement.style.background = 'transparent';
            this.labelElement.style.color = '#3c3c3c';
            return;
        }
        this.lineMaterial.color.set(PREVIEW_LINE_COLOR);
        this.lineMaterial.opacity = 0.55;
        this.labelElement.style.background = 'rgba(30, 111, 255, 0.75)';
        this.labelElement.style.color = '#fff';
    }

    public update(
        candidate: SmartDimensionCandidate,
        anchorWorldResolver: ReferencePlane,
        pointerLocal: SmartDimensionVec2,
    ): void {
        this.applyStyle('preview');

        const layoutAnchors = buildPreviewLayoutAnchors(candidate);
        const segments: SketchDimensionLayoutSegment[] = [];
        let labelLocal: SmartDimensionVec2 = pointerLocal;

        if (layoutAnchors) {
            // 完整尺寸样式：标注位置=指针，布局纯函数产出尺寸线/界线/箭头/弧。
            const previewSnapshot: SketchDimensionSnapshot = {
                dimensionId: 'cad:sketch-dimension:__preview__',
                sketchId: '__preview__',
                name: '__preview__',
                dimensionKind: candidate.dimensionKind,
                space: 'sketch-2d',
                role: 'driving',
                suppressed: false,
                readOnly: false,
                parameter: {
                    parameterId: '__preview__',
                    nominalValue: candidate.currentValue,
                    quantityKind: resolveSketchDimensionQuantityKind(candidate.dimensionKind),
                    valueScale: candidate.valueScale,
                    expression: null,
                },
                measuredValue: null,
                references: [],
                branch: candidate.branch,
                display: {labelPosition: pointerLocal},
                solveState: 'unknown',
                repair: null,
            };
            const layout = resolveSketchDimensionLayout(previewSnapshot, layoutAnchors);

            segments.push(
                ...layout.segments,
                ...layout.arrows.flatMap((arrow) => expandSketchDimensionArrow(arrow)),
            );
            layout.arcs.forEach((arc) => {
                const sweep = arc.endAngle - arc.startAngle;

                for (let index = 0; index < ARC_SAMPLE_SEGMENTS; index += 1) {
                    const fromAngle = arc.startAngle + (sweep * index) / ARC_SAMPLE_SEGMENTS;
                    const toAngle = arc.startAngle + (sweep * (index + 1)) / ARC_SAMPLE_SEGMENTS;

                    segments.push({
                        start: [
                            arc.center[0] + Math.cos(fromAngle) * arc.radius,
                            arc.center[1] + Math.sin(fromAngle) * arc.radius,
                        ],
                        end: [
                            arc.center[0] + Math.cos(toAngle) * arc.radius,
                            arc.center[1] + Math.sin(toAngle) * arc.radius,
                        ],
                    });
                }
            });
            labelLocal = layout.textPosition;
        } else {
            // 特殊类型退回锚点引线预览。
            candidate.previewAnchors.forEach((anchor) => {
                segments.push({start: anchor, end: pointerLocal});
            });
        }

        const position = this.lineBuffer.beginUpdate(segments.length * 2);

        segments.forEach((segment, index) => {
            anchorWorldResolver.localToWorldTo(segment.start[0], segment.start[1], this.worldPoint);
            position.setXYZ(index * 2, this.worldPoint.x, this.worldPoint.y, this.worldPoint.z);
            anchorWorldResolver.localToWorldTo(segment.end[0], segment.end[1], this.worldPoint);
            position.setXYZ(index * 2 + 1, this.worldPoint.x, this.worldPoint.y, this.worldPoint.z);
        });
        this.line.geometry = this.lineBuffer.endUpdate();

        this.labelElement.textContent = SketchSmartDimensionPreviewRenderer.formatValue(candidate);
        anchorWorldResolver.localToWorldTo(labelLocal[0], labelLocal[1], this.label.position);
        this.group.visible = true;
    }

    /** 隐藏预览（保留资源）。 */
    public hide(): void {
        this.group.visible = false;
    }

    /**
     * 格式化候选值（UI 边界单位：长度毫米、角度度）。
     *
     * @param candidate 候选。
     * @returns 显示文本。
     */
    public static formatValue(candidate: SmartDimensionCandidate): string {
        const isAngle = candidate.dimensionKind === 'angle'
            || candidate.dimensionKind === 'three-point-angle'
            || candidate.dimensionKind === 'symmetric-angle';

        if (isAngle) {
            return `${(candidate.currentValue * 180 / Math.PI).toFixed(2)}°`;
        }

        const prefix = candidate.dimensionKind === 'diameter'
            ? '⌀'
            : candidate.dimensionKind === 'radius' ? 'R' : '';

        return `${prefix}${convertMToMm(candidate.currentValue).toFixed(2)}mm`;
    }

    /** 释放全部资源。 */
    public dispose(): void {
        this.detach();
        this.group.remove(this.line);
        this.group.remove(this.label);
        this.lineBuffer.dispose();
        this.lineMaterial.dispose();
        this.labelElement.remove();
    }
}
